<template>
  <div class="language-switcher" :class="{ 'dark-bg': dark }">
    <div class="language-toggle" @click="toggleDropdown">
      <span class="flag">{{ getFlagEmoji(currentLanguageCode) }}</span>
      <span class="code">{{ currentLanguageCode.toUpperCase() }}</span>
      <i class="fas fa-caret-down" :class="{ 'rotate': isOpen }"></i>
    </div>
    <div v-if="isOpen" class="language-submenu">
      <div v-if="availableLanguages.length === 0" style="padding: 10px; color: #999;">
        Loading languages...
      </div>
      <a
        v-for="language in availableLanguages"
        :key="language.code"
        href="#"
        @click.prevent="selectLanguage(language.code)"
        :class="{ 'active': language.code === currentLanguageCode }"
      >
        <span class="flag">{{ getFlagEmoji(language.code) }}</span>
        <span class="lang-name">{{ language.name }}</span>
      </a>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'LanguageSwitcher',
  props: {
    dark: {
      type: Boolean,
      default: false
    }
  },

  setup() {
    const store = useStore();
    const isOpen = ref(false);
    const loading = ref(false);

    const currentLanguageCode = computed(() => store.getters['translations/currentLanguage']);
    const availableLanguages = computed(() => {
      const langs = store.getters['translations/availableLanguages'];
      console.log('Available languages in switcher:', langs);
      return langs;
    });

    // Flag emoji mapping
    const flagMap = {
      'en': '🇺🇸',
      'es': '🇪🇸',
      'fr': '🇫🇷',
      'de': '🇩🇪',
      'it': '🇮🇹',
      'zh': '🇨🇳',
      'ja': '🇯🇵',
      'ko': '🇰🇷',
      'tr': '🇹🇷',
      'pl': '🇵🇱',
      'nl': '🇳🇱'
    };

    const getFlagEmoji = (code) => {
      return flagMap[code] || '🌐';
    };

    const toggleDropdown = () => {
      console.log('Toggle dropdown clicked, current state:', isOpen.value);
      console.log('Available languages:', availableLanguages.value);
      isOpen.value = !isOpen.value;
      console.log('New state:', isOpen.value);
    };

    const selectLanguage = async (languageCode) => {
      if (languageCode === currentLanguageCode.value) {
        isOpen.value = false;
        return;
      }

      try {
        loading.value = true;
        await store.dispatch('translations/setLanguage', languageCode);
        console.log(`Language changed to: ${languageCode}`);

        // Emit custom event to notify components that language changed
        const event = new CustomEvent('language-changed', {
          detail: { languageCode }
        });
        window.dispatchEvent(event);
        console.log('Dispatched language-changed event');

        isOpen.value = false;
      } catch (error) {
        console.error('Error changing language:', error);
      } finally {
        loading.value = false;
      }
    };

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      const switcher = document.querySelector('.language-switcher');
      if (switcher && !switcher.contains(event.target)) {
        isOpen.value = false;
      }
    };

    watch(isOpen, (newVal) => {
      if (newVal) {
        document.addEventListener('click', handleClickOutside);
      } else {
        document.removeEventListener('click', handleClickOutside);
      }
    });

    return {
      isOpen,
      currentLanguageCode,
      availableLanguages,
      loading,
      toggleDropdown,
      selectLanguage,
      getFlagEmoji
    };
  }
};
</script>

<style scoped>
.language-switcher {
  position: relative;
  display: inline-block;
}

.language-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  cursor: pointer;
  user-select: none;
  color: #333;
  font-weight: 500;
  transition: opacity 0.2s;
}

.language-toggle:hover {
  opacity: 0.7;
}

/* White text for dark backgrounds (home/landing pages) */
.dark-bg .language-toggle {
  color: #fff;
}

.dark-bg .language-toggle:hover {
  opacity: 0.8;
}

.language-toggle .flag {
  font-size: 18px;
  line-height: 1;
}

.language-toggle .code {
  font-size: 14px;
  font-weight: 600;
}

.language-toggle .fa-caret-down {
  font-size: 12px;
  transition: transform 0.2s;
}

.language-toggle .fa-caret-down.rotate {
  transform: rotate(180deg);
}

.language-submenu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  z-index: 1000;
  margin-top: 4px;
}

.language-submenu a {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  text-decoration: none;
  color: #333;
  transition: background-color 0.2s;
}

.language-submenu a:hover {
  background-color: #f5f5f5;
}

.language-submenu a.active {
  background-color: #e3f2fd;
  color: #1976d2;
  font-weight: 600;
}

.language-submenu a .flag {
  font-size: 18px;
  line-height: 1;
}

.language-submenu a .lang-name {
  font-size: 14px;
}
</style>
